import { FormEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  Moon,
  PackageCheck,
  ShieldCheck,
  ShoppingBag,
  Snowflake,
  Sparkles,
  Star
} from "lucide-react";
import { funnel, generatedAt } from "./data/funnel";
import type { OfferTier } from "./lib/types";

const money = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2
  }).format(cents / 100);

const useRoute = () => {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigate = (href: string) => {
    window.history.pushState({}, "", href);
    setPath(href);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { path, navigate };
};

const AppLink = ({
  href,
  children,
  className,
  navigate
}: {
  href: string;
  children: ReactNode;
  className?: string;
  navigate: (href: string) => void;
}) => {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (href.startsWith("/")) {
      event.preventDefault();
      navigate(href);
    }
  };

  return (
    <a className={className} href={href} onClick={handleClick}>
      {children}
    </a>
  );
};

const Header = ({ navigate }: { navigate: (href: string) => void }) => (
  <header className="site-header">
    <AppLink className="brand-lockup" href="/" navigate={navigate}>
      <span className="brand-mark">{funnel.brand.slice(0, 1)}</span>
      <span>{funnel.brand}</span>
    </AppLink>
    <nav aria-label="Primary navigation">
      <AppLink href="/" navigate={navigate}>
        Landing
      </AppLink>
      <AppLink href="/sales" navigate={navigate}>
        Sales
      </AppLink>
      <AppLink className="nav-cta" href="/checkout" navigate={navigate}>
        Checkout
      </AppLink>
    </nav>
  </header>
);

const Footer = ({ navigate }: { navigate: (href: string) => void }) => (
  <footer className="footer-band">
    <div>
      <strong>{funnel.brand}</strong>
      <p>{funnel.product.liveSourceStatus}</p>
      <p>Generated data timestamp: {new Date(generatedAt).toLocaleString()}</p>
    </div>
    <div className="footer-links">
      <AppLink href="/sales" navigate={navigate}>
        Sales page
      </AppLink>
      <AppLink href="/checkout" navigate={navigate}>
        Checkout skeleton
      </AppLink>
      <a href="/assets/generated/prompts.md">Image prompts</a>
    </div>
  </footer>
);

const Hero = ({ navigate }: { navigate: (href: string) => void }) => (
  <section
    className="hero"
    style={{ backgroundImage: `linear-gradient(90deg, rgba(4, 8, 12, 0.86), rgba(4, 8, 12, 0.46), rgba(4, 8, 12, 0.08)), url(${funnel.assets.hero})` }}
  >
    <div className="hero-copy">
      <p className="eyebrow">{funnel.landing.eyebrow}</p>
      <h1>{funnel.landing.headline}</h1>
      <p className="hero-subhead">{funnel.landing.subhead}</p>
      <div className="hero-actions">
        {funnel.landing.ctas.map((cta, index) => (
          <AppLink
            className={index === 0 ? "button button-primary" : "button button-secondary"}
            href={cta.href}
            key={cta.href}
            navigate={navigate}
          >
            {cta.label}
            {index === 0 ? <ShoppingBag size={18} aria-hidden="true" /> : <ArrowRight size={18} aria-hidden="true" />}
          </AppLink>
        ))}
      </div>
    </div>
  </section>
);

const ProofStrip = () => (
  <section className="proof-strip" aria-label="Key product proof points">
    {funnel.landing.proofPoints.map((point) => (
      <div className="proof-point" key={point}>
        <CheckCircle2 size={18} aria-hidden="true" />
        <span>{point}</span>
      </div>
    ))}
  </section>
);

const LandingPage = ({ navigate }: { navigate: (href: string) => void }) => (
  <>
    <Hero navigate={navigate} />
    <ProofStrip />
    <main>
      <section className="image-text-section">
        <div className="section-copy">
          <p className="eyebrow">Built around the real day</p>
          <h2>{funnel.strategy.throughLine}</h2>
          <p>{funnel.strategy.positioning}</p>
          <div className="feature-row">
            <span>
              <Moon size={18} aria-hidden="true" /> Soft enough to sink into
            </span>
            <span>
              <Snowflake size={18} aria-hidden="true" /> Cool-touch comfort option
            </span>
          </div>
        </div>
        <img src={funnel.assets.product} alt={`${funnel.product.shortName} product detail`} />
      </section>

      {funnel.landing.sections.map((section, index) => (
        <section className={index % 2 === 0 ? "content-band" : "content-band alt-band"} key={section.title}>
          <div className="content-inner">
            <p className="eyebrow">{section.eyebrow}</p>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
            <ul className="check-list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>
                  <CheckCircle2 size={18} aria-hidden="true" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ))}

      <OfferTiers navigate={navigate} />
    </main>
  </>
);

const OfferTiers = ({
  navigate,
  selectedTierId,
  onSelect
}: {
  navigate?: (href: string) => void;
  selectedTierId?: string;
  onSelect?: (id: string) => void;
}) => (
  <section className="offer-section">
    <div className="section-heading">
      <p className="eyebrow">Pricing and offer</p>
      <h2>Choose the sleep upgrade that fits the bed.</h2>
      <p>{funnel.offer.strategy}</p>
    </div>
    <div className="tier-grid">
      {funnel.offer.tiers.map((tier) => (
        <button
          className={`tier-card ${selectedTierId === tier.id ? "selected" : ""}`}
          key={tier.id}
          onClick={() => {
            if (onSelect) {
              onSelect(tier.id);
              return;
            }
            navigate?.("/checkout");
          }}
          type="button"
        >
          {tier.badge ? <span className="tier-badge">{tier.badge}</span> : null}
          <span className="tier-label">{tier.label}</span>
          <strong>{money(tier.price)}</strong>
          {tier.compareAt ? <span className="compare">Compare at {money(tier.compareAt)}</span> : null}
          <span className="unit-price">{tier.unitPrice}</span>
          <p>{tier.description}</p>
          <ul>
            {tier.items.map((item) => (
              <li key={item}>
                <CheckCircle2 size={16} aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
          {navigate && !onSelect ? (
            <span className="button button-mini">
              Choose {tier.shortLabel}
              <ArrowRight size={16} aria-hidden="true" />
            </span>
          ) : null}
        </button>
      ))}
    </div>
    {navigate ? (
      <div className="center-action">
        <AppLink className="button button-primary" href="/checkout" navigate={navigate}>
          Continue to checkout
          <CreditCard size={18} aria-hidden="true" />
        </AppLink>
      </div>
    ) : null}
  </section>
);

const Reviews = () => (
  <section className="reviews-band">
    <div className="section-heading">
      <p className="eyebrow">Social proof section</p>
      <h2>Review excerpts from the source PDP.</h2>
      <p>These excerpts come from the parsed TikTok Shop product data; ratings and counts can drift over time.</p>
    </div>
    <div className="review-grid">
      {funnel.sales.reviews.map((review) => (
        <article className="review-card" key={review.name}>
          <div className="stars" aria-label={`${review.rating || 5} star review excerpt`}>
            {Array.from({ length: review.rating || 5 }).map((_, index) => (
              <Star fill="currentColor" key={index} size={16} aria-hidden="true" />
            ))}
          </div>
          <p>{review.quote}</p>
          <span>{review.name}</span>
          <small>{review.label}</small>
        </article>
      ))}
    </div>
  </section>
);

const SalesPage = ({ navigate }: { navigate: (href: string) => void }) => (
  <main>
    <section className="sales-hero">
      <div className="sales-copy">
        <p className="eyebrow">{funnel.brand} sales page</p>
        <h1>{funnel.sales.headline}</h1>
        <p>{funnel.sales.lead}</p>
        <AppLink className="button button-primary" href="/checkout" navigate={navigate}>
          Choose a bundle
          <ShoppingBag size={18} aria-hidden="true" />
        </AppLink>
      </div>
      <img src={funnel.assets.lifestyle} alt={`${funnel.product.shortName} in a calm bedroom setting`} />
    </section>

    {funnel.sales.sections.map((section, index) => (
      <section className={index % 2 === 0 ? "sales-section" : "sales-section alt-band"} key={section.title}>
        <div className="content-inner">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.bullets ? (
            <ul className="check-list">
              {section.bullets.map((bullet) => (
                <li key={bullet}>
                  <CheckCircle2 size={18} aria-hidden="true" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>
    ))}

    <section className="image-text-section reverse">
      <div className="section-copy">
        <p className="eyebrow">Fabric story</p>
        <h2>{funnel.strategy.corePromise}</h2>
        <p>{funnel.product.description}</p>
      </div>
      <img src={funnel.assets.fabric} alt={`${funnel.product.shortName} quilted puff-grid fabric detail`} />
    </section>

    <OfferTiers navigate={navigate} />
    <Reviews />

    <section className="faq-section">
      <div className="section-heading">
        <p className="eyebrow">Checkout notes</p>
        <h2>Built for review now, ready for Stripe keys later.</h2>
      </div>
      <div className="faq-grid">
        {funnel.sales.faq.map((item) => (
          <article key={item.question}>
            <h3>{item.question}</h3>
            <p>{item.answer}</p>
          </article>
        ))}
      </div>
    </section>
  </main>
);

const CheckoutPage = () => {
  const defaultTier =
    funnel.offer.tiers.find((tier) => tier.id === funnel.checkout.defaultTierId) || funnel.offer.tiers[0];
  const [tierId, setTierId] = useState(defaultTier.id);
  const [size, setSize] = useState(defaultTier.defaultSize || defaultTier.allowedSizes?.[0] || funnel.checkout.sizeOptions[0]);
  const [color, setColor] = useState(
    defaultTier.defaultColor || defaultTier.allowedColors?.[0] || funnel.checkout.colorOptions[0].id
  );
  const [message, setMessage] = useState(() => {
    const status = new URLSearchParams(window.location.search).get("status");
    if (status === "success") return "Stripe returned a success status placeholder.";
    if (status === "cancelled") return "Stripe returned a cancelled status placeholder.";
    return "";
  });
  const [isLoading, setIsLoading] = useState(false);

  const selectedTier = useMemo<OfferTier>(
    () => funnel.offer.tiers.find((tier) => tier.id === tierId) || defaultTier,
    [defaultTier, tierId]
  );
  const availableSizes = selectedTier.allowedSizes || funnel.checkout.sizeOptions;
  const availableColors = funnel.checkout.colorOptions.filter((option) =>
    (selectedTier.allowedColors || funnel.checkout.colorOptions.map((colorOption) => colorOption.id)).includes(option.id)
  );

  const selectTier = (id: string) => {
    const nextTier = funnel.offer.tiers.find((tier) => tier.id === id);
    if (!nextTier) return;
    setTierId(id);
    setSize(nextTier.defaultSize || nextTier.allowedSizes?.[0] || funnel.checkout.sizeOptions[0]);
    setColor(nextTier.defaultColor || nextTier.allowedColors?.[0] || funnel.checkout.colorOptions[0].id);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setMessage("");

    try {
      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          variantId: selectedTier.id,
          size,
          color
        })
      });

      const payload = (await response.json().catch(() => ({}))) as {
        url?: string;
        error?: string;
      };

      if (payload.url) {
        window.location.assign(payload.url);
        return;
      }

      setMessage(
        payload.error ||
          "Checkout is currently stubbed. Add Stripe environment variables in Cloudflare Pages to enable redirect."
      );
    } catch {
      setMessage("Checkout function is not reachable from this local server. Use npm run pages:dev to test the stub path.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main>
      <section className="checkout-hero">
        <p className="eyebrow">Stripe-hosted checkout skeleton</p>
        <h1>{funnel.checkout.title}</h1>
        <p>{funnel.checkout.subtitle}</p>
      </section>

      <form className="checkout-layout" onSubmit={submit}>
        <div className="checkout-main">
          <OfferTiers selectedTierId={tierId} onSelect={selectTier} />

          <section className="selector-band" aria-label={`${funnel.checkout.sizeLabel} and ${funnel.checkout.colorLabel} choices`}>
            <div>
              <h2>{funnel.checkout.sizeLabel}</h2>
              <div className="segmented-control">
                {availableSizes.map((option) => (
                  <button
                    className={size === option ? "active" : ""}
                    key={option}
                    onClick={() => setSize(option)}
                    type="button"
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <h2>{funnel.checkout.colorLabel}</h2>
              <div className="swatch-row">
                {availableColors.map((option) => (
                  <button
                    aria-label={option.label}
                    className={color === option.id ? "swatch active" : "swatch"}
                    key={option.id}
                    onClick={() => setColor(option.id)}
                    style={{ background: option.swatch }}
                    title={option.label}
                    type="button"
                  />
                ))}
              </div>
            </div>
          </section>
        </div>

        <aside className="checkout-summary" aria-label="Order summary">
          <PackageCheck size={28} aria-hidden="true" />
          <h2>{selectedTier.label}</h2>
          <p>{selectedTier.description}</p>
          <strong>{money(selectedTier.price)}</strong>
          <span>{selectedTier.unitPrice}</span>
          <div className="summary-line">
            <span>Size</span>
            <b>{size}</b>
          </div>
          <div className="summary-line">
            <span>Color</span>
            <b>{funnel.checkout.colorOptions.find((option) => option.id === color)?.label || color}</b>
          </div>
          <div className="security-note">
            <ShieldCheck size={18} aria-hidden="true" />
            <span>Raw card data goes browser to Stripe, never to this repo.</span>
          </div>
          <button className="button button-primary checkout-button" disabled={isLoading} type="submit">
            {isLoading ? "Preparing checkout..." : "Continue to Stripe"}
            <CreditCard size={18} aria-hidden="true" />
          </button>
          {message ? <p className="checkout-message">{message}</p> : null}
        </aside>
      </form>
    </main>
  );
};

export const App = () => {
  const { path, navigate } = useRoute();
  const page = path.startsWith("/sales") ? (
    <SalesPage navigate={navigate} />
  ) : path.startsWith("/checkout") ? (
    <CheckoutPage />
  ) : (
    <LandingPage navigate={navigate} />
  );

  return (
    <>
      <Header navigate={navigate} />
      {page}
      <Footer navigate={navigate} />
    </>
  );
};
